/**
 * 화면 설명
 * ---------------------------------------------------------------------------
 * 모든 화면 오른쪽 위에 같은 모양으로 놓이는 물음표다. 누르면 "이 화면이
 * 무엇이고, 여기 넣은 값이 어디에 쓰이는지" 를 보여 준다.
 *
 * 왜 상시 안내문이 아니라 아이콘인가
 *   화면마다 긴 설명을 붙여 두면 처음 하루는 도움이 되고 그다음부터는 매번
 *   지나쳐야 하는 장애물이 된다. 매일 쓰는 사람이 대다수다. 그래서 설명은
 *   접어 두고, **필요한 사람이 같은 자리에서 꺼내 볼 수 있게** 한다.
 *
 *   "같은 자리" 가 핵심이다. 화면마다 다른 곳에 있으면 찾는 것 자체가 일이다.
 *
 * 예외 — 상시로 보여야 하는 것
 *   "시설을 먼저 등록해야 한다" 처럼 **지금 막힌 이유**는 접어 두면 안 된다.
 *   그건 각 화면에서 Alert 로 따로 보여 준다. 이 부품은 배경 설명을 맡는다.
 */

import { useState, type ReactNode } from 'react';
import { Button, Modal, Tooltip, Typography } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';

export interface PageHelpProps {
  /** 설명 창의 제목. 보통 화면 이름 */
  title: string;
  /** 설명 본문 */
  children: ReactNode;
  /** 아이콘만 두지 않고 글자를 함께 보일지. 처음 보는 화면에서는 켜 둔다 */
  withLabel?: boolean;
}

export function PageHelp({ title, children, withLabel = false }: PageHelpProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Tooltip title="이 화면 설명">
        <Button
          type="text"
          size="small"
          icon={<QuestionCircleOutlined />}
          onClick={() => setOpen(true)}
          aria-label={`${title} 화면 설명`}
        >
          {withLabel ? '화면 설명' : null}
        </Button>
      </Tooltip>

      {/*
        넓게 연다.

        640px 에서는 「기본 제공 + 수량당 추가」 같은 짧은 표도 줄이 꺾여, 두 줄로
        접힌 설명을 읽느라 오히려 품이 들었다. 좁은 창에서는 화면 너비의 92% 까지
        쓰고, 넓은 창에서도 920px 을 넘기지 않는다 — 글줄이 그보다 길어지면 눈이
        다음 줄 첫머리를 놓친다.

        본문은 따로 스크롤시킨다. 설명이 긴 화면(요금제 등)에서 창 전체가 길어지면
        닫기 단추가 화면 밖으로 밀려난다.
      */}
      <Modal
        open={open}
        title={title}
        onCancel={() => setOpen(false)}
        footer={
          <Button type="primary" onClick={() => setOpen(false)}>
            닫기
          </Button>
        }
        width="92%"
        style={{ maxWidth: 920, top: 40 }}
        styles={{ body: { maxHeight: 'calc(100vh - 220px)', overflowY: 'auto' } }}
      >
        <div style={{ marginTop: 8 }}>{children}</div>
      </Modal>
    </>
  );
}

/**
 * 설명 본문에서 쓰는 작은 조각들.
 * 화면마다 제각각 꾸미면 같은 기능인지 알아보기 어려워진다.
 */

/** 한 문단 */
export function HelpP({ children }: { children: ReactNode }) {
  return <p style={{ marginBottom: 12, lineHeight: 1.7 }}>{children}</p>;
}

/** 작은 제목 */
export function HelpH({ children }: { children: ReactNode }) {
  return (
    <Typography.Text strong style={{ display: 'block', marginBottom: 6 }}>
      {children}
    </Typography.Text>
  );
}

/** 항목 설명 목록. "이 칸이 무엇인가" 를 줄줄이 적을 때 */
export function HelpList({ items }: { items: Array<{ term: string; desc: ReactNode }> }) {
  return (
    <ul style={{ margin: '0 0 12px 0', paddingLeft: 18, lineHeight: 1.8 }}>
      {items.map((it) => (
        <li key={it.term}>
          <b>{it.term}</b> — {it.desc}
        </li>
      ))}
    </ul>
  );
}

/** 어디로 가야 하는지 가리키는 경로 표시 */
export function HelpPath({ children }: { children: ReactNode }) {
  return (
    <Typography.Text code style={{ whiteSpace: 'nowrap' }}>
      {children}
    </Typography.Text>
  );
}
